import { useEffect, useState } from 'react';

/**
 * Minimal router.
 *
 * Most routes live under `#/…` (e.g. `#/work/lead-capture-crm-sync`) so that every
 * existing in-page anchor (`#work`, `#contact`, …) keeps behaving as a plain
 * jump link: anything that doesn't start with `#/` is treated as "home, then
 * scroll to that element".
 *
 * Landing pages that get shared as clean URLs (e.g. `/real-estate`) are real
 * paths instead. The build writes a copy of index.html for each of them (see
 * scripts/prerender-routes.js), so the static nginx deploy serves them directly.
 */

/** Page served at its own URL path rather than under `#/`. */
export const REAL_ESTATE_PATH = '/real-estate';

/** The document's URL path without a trailing slash — `''` on the home document. */
function readPathname(): string {
    if (typeof window === 'undefined') return '';
    return window.location.pathname.replace(/\/+$/, '');
}

/**
 * The current route path — `''` on the home page, `/work/<slug>` on a detail
 * page, or the document path (e.g. `/real-estate`) for path-based pages.
 */
export function readPath(): string {
    if (typeof window === 'undefined') return '';
    const pathname = readPathname();
    if (pathname) return pathname;
    const hash = window.location.hash;
    return hash.startsWith('#/') ? hash.slice(1) : '';
}

/** The current in-page anchor id, e.g. `work` — empty while on a `#/` route. */
export function readAnchor(): string {
    if (typeof window === 'undefined') return '';
    const hash = window.location.hash;
    return hash.length > 1 && !hash.startsWith('#/') ? hash.slice(1) : '';
}

/** Subscribes to hash changes and re-renders with the current route path. */
export function useRoutePath(): string {
    const [path, setPath] = useState(readPath);

    useEffect(() => {
        const onChange = () => setPath(readPath());
        window.addEventListener('hashchange', onChange);
        return () => window.removeEventListener('hashchange', onChange);
    }, []);

    return path;
}

/**
 * Resolves a site link. Home anchors and hash routes (`#services`, `#/work/x`)
 * get a leading `/` when the current document isn't the home page, so they land
 * back on it; on the home page they stay bare so the jump is same-document and
 * keeps any query string (e.g. UTM tags). Other hrefs pass through unchanged.
 */
export function siteHref(href: string): string {
    return href.startsWith('#') && readPathname() ? `/${href}` : href;
}

/** `href` for a route link. Use on plain anchors so middle-click/new-tab still work. */
export function routeHref(path: string): string {
    return siteHref(`#${path}`);
}
